import React, { useRef, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Text } from 'recharts';

const ResponsiveBarChart = ({
	data,
	dataKey,
	fill = '#0088FE',
	labelStyle = 'slanted', // Can be 'slanted', 'vertical', or 'onBar'
	onBarClick,
}) => {
	const containerRef = useRef(null);
	const [chartWidth, setChartWidth] = useState(0);

	useEffect(() => {
		const cRef = containerRef.current;
		const updateDimensions = () => {
			if (cRef) {
				const containerWidth = cRef.getBoundingClientRect().width;
				const width = Math.max(containerWidth - 20, 300);
				if (width !== chartWidth) {
					setChartWidth(width);
				}
			}
		};

		const resizeObserver = new ResizeObserver(updateDimensions);
		if (cRef) {
			resizeObserver.observe(cRef);
		}
		updateDimensions();

		return () => {
			if (cRef) {
				resizeObserver.unobserve(cRef);
			}
			resizeObserver.disconnect();
		};
	}, [chartWidth]);

	// Custom label component for bars
	const CustomBarLabel = ({ x, y, width, value }) => {
		return (
			<text x={x + width / 2} y={y - 5} fill="#666" textAnchor="middle" fontSize={12}>
				{value}
			</text>
		);
	};

	// Function to render custom tick with different styles
	const CustomXAxisTick = ({ x, y, payload }) => {
		switch (labelStyle) {
			case 'vertical':
				return (
					<g transform={`translate(${x},${y})`}>
						<text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-90)">
							{payload.value}
						</text>
					</g>
				);
			case 'slanted':
				return (
					<g transform={`translate(${x},${y})`}>
						<text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-45)">
							{payload.value}
						</text>
					</g>
				);
			default:
				return (
					<g transform={`translate(${x},${y})`}>
						<text x={0} y={0} dy={16} textAnchor="middle" fill="#666">
							{payload.value}
						</text>
					</g>
				);
		}
	};

	if (chartWidth === 0) {
		return <div ref={containerRef} style={{ width: '100%' }} />;
	}

	const chartMargin =
		labelStyle === 'vertical'
			? { top: 20, right: 20, left: 20, bottom: 100 }
			: labelStyle === 'slanted'
			? { top: 20, right: 20, left: 20, bottom: 60 }
			: { top: 20, right: 20, left: 20, bottom: 20 };

	return (
		<div ref={containerRef} style={{ width: '100%' }}>
			<BarChart width={chartWidth} height={400} data={data} margin={chartMargin}>
				<XAxis
					dataKey="name"
					textAnchor="end"
					angle={-45}
					fontSize={12}
					tickFormatter={value => {
						const truncated = value.length > 15 ? value.substring(0, 10) + '...' : value;
						const capitalized = truncated
							.toLowerCase()
							.split(' ')
							.map(word => word.charAt(0).toUpperCase() + word.slice(1))
							.join(' ');
						return capitalized;
					}}
					interval={0}
				/>
				<YAxis />
				<Tooltip />
				<Bar
					dataKey={dataKey}
					fill={fill}
					label={labelStyle === 'onBar' ? <CustomBarLabel /> : null}
					onClick={onBarClick ? data => onBarClick(data?.payload || data) : undefined}
					cursor={onBarClick ? 'pointer' : undefined}
				/>
			</BarChart>
		</div>
	);
};

export default ResponsiveBarChart;
